/**
 * Client-side UI feature flags for the AI search surface (Search Graft Track A).
 *
 * Why not a BuilderFlag: `BuilderFlags` is a mapped type over the server-generated
 * `BTAdminBuilderPreference` enum (`@btnet/services-btnet`), so adding one requires
 * a server enum change — out of scope for a client-only rendering PR. These flags
 * gate pure rendering behavior (how the client paints an SSE stream), carry no
 * per-builder product semantics, and need a support/dev override path. The repo's
 * convention for exactly that shape is a typed `BTLocalStorage` boolean key
 * (cf. "bt-boolean-disableAllAnalytics"), which is what backs these.
 *
 * Resolution order for each flag:
 *   1. explicit localStorage override (support can flip a single user's browser
 *      from the console: `localStorage["bt-boolean-aiSearchStableList"] = "false"`)
 *   2. default ON — every environment, every build mode.
 */
import { BTLocalStorage } from "types/btStorage";

/**
 * PR-A1 (ADO 286301): buffered commit-once rendering for the AI search list.
 *
 * ON  — result events buffer invisibly; the fused list commits to visible state
 *       exactly once (at `done`, or when a 600ms stability window elapses with
 *       all launched ops complete); late ops append below the committed list;
 *       skeletons show until the first commit.
 * OFF — legacy behavior: every result event re-fuses and repaints the list
 *       (the 4-8 visible reshuffles pinned by the T4 baseline suite).
 *
 * DEFAULT: ON in every build mode (graft 2026-08-28). The original A1 rollout
 * pin — ON in dev/test only, OFF in production builds — was a safety leash
 * for the window when only the harness had exercised the stable path. The
 * first production-posture build real users touched (the ephemeral) ran the
 * legacy path and reproduced exactly the founding UX bug Track A exists to
 * kill: results paint fast, then twitch and reshuffle 1-2 seconds later as
 * each op re-fuses. The stable list IS the a5 deliverable, so the graft
 * branch ships it on by default; the localStorage override above remains
 * the per-browser kill-switch (set the key to false to fall back to the
 * legacy path, whose behavior stays pinned by the T4 baseline suites).
 */
export function isAiSearchStableListEnabled(): boolean {
    const override = BTLocalStorage.get("bt-boolean-aiSearchStableList");
    if (override !== null && override !== undefined) return override;
    return true;
}

/**
 * graft/A6 (ADO 288541): the full-page AI search surface.
 *
 * ON  — the nav search box routes to `/Search`, which owns the query (`?q=`),
 *       the result list, and the QA slot. Results are linkable and browser
 *       history works.
 * OFF — the pre-A6 dropdown panel anchored under the nav bar.
 *
 * DEFAULT: OFF. The page is built dark across A6.2-A6.5 and reachable only by
 * typing the URL; the A6.6 cutover flips this default and retires the panel.
 * Support/dev can opt a single browser in from the console:
 * `localStorage["bt-boolean-aiSearchFullPage"] = "true"`.
 */
export function isAiSearchFullPageEnabled(): boolean {
    const override = BTLocalStorage.get("bt-boolean-aiSearchFullPage");
    if (override !== null && override !== undefined) return override;
    return false;
}
